import { useState, useEffect, useMemo } from 'react';
import './menu.css';
import contacto from '../../../data/contacto.json';
import { useMenuData } from '../../../Hooks/useMenuData';
import { PROMO_ITEMS } from '../../../Constants/PromoItems';
import { DIAS } from '../../../Constants/Admin';
import SeccionMenu, { SeccionHeader, SubHeader } from './SeccionMenu';

const LOGO = '/assets/imgs/logo-sin-fondo.webp';
const HERO_IMG = 'https://images.unsplash.com/photo-1513104890138-7c749659a591?w=1600&q=80';

export default function Menu() {
  const { secciones, productos, promos } = useMenuData();
  const [menuOpen, setMenuOpen] = useState(false);

  const hoyNombre = DIAS[new Date().getDay()];
  const promosActivas = promos.filter(p => p.activa !== false);
  const promoHoy = promosActivas.find(p => p.dia === hoyNombre);
  const promoMostrar = promoHoy || promosActivas[0];

  // Secciones principales con sus subsecciones y productos ya ordenados
  const principales = useMemo(() => {
    const porOrden = (a, b) => (a.orden ?? 0) - (b.orden ?? 0);
    const activas = secciones.filter(s => s.activa !== false);
    const productosDe = (id) => productos.filter(p => p.seccionId === id).sort(porOrden);
    return activas
      .filter(s => !s.padreId)
      .sort(porOrden)
      .map(s => ({
        ...s,
        productos: productosDe(s.seccionId),
        hijos: activas
          .filter(h => h.padreId === s.seccionId)
          .sort(porOrden)
          .map(h => ({ ...h, productos: productosDe(h.seccionId) })),
      }));
  }, [secciones, productos]);

  const navLinks = [
    ...(promoMostrar ? [{ href: '#promos', label: 'Promos' }] : []),
    ...principales.map(s => ({ href: `#${s.seccionId}`, label: s.navLabel || s.nombre })),
  ];
  const primeraSeccion = principales[0] ? `#${principales[0].seccionId}` : '#promos';

  const closeMenu = () => setMenuOpen(false);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMenuOpen(false);
    setTimeout(() => {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 350);
  };

  // Animación de las fotos al entrar/salir de pantalla (sube y baja el scroll).
  // Se vuelve a enganchar cuando llega el menú de la API (pueden cambiar las fotos).
  useEffect(() => {
    const targets = document.querySelectorAll('.alitas-foto');
    if (!targets.length || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle('reveal-in', entry.isIntersecting);
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -80px 0px' }
    );

    targets.forEach((el) => {
      el.classList.add('reveal-foto');
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [principales]);

  return (
    <>
      {/* TOP BAR */}
      <div className="top-bar">
        <div className="inner">
          <div className="topbar-logo" style={{ backgroundImage: `url(${LOGO})` }} />
          <a href={contacto.facebook_url} target="_blank" rel="noreferrer">
            <span className="icon">📘</span> {contacto.facebook}
          </a>
          <a href={`https://wa.me/52${contacto.whatsapp}`} target="_blank" rel="noreferrer">
            <span className="icon">📱</span> {contacto.whatsapp}
          </a>
          <a href={`tel:${contacto.telefono}`}>
            <span className="icon">📞</span> {contacto.telefono}
          </a>
        </div>
      </div>

      {/* HERO */}
      <section className="hero">
        <div className="hero-img-wrap">
          <img src={HERO_IMG} alt="Pizza Planeta" />
        </div>
        <div className="hero-logo-ship">
          <img src={LOGO} alt="" aria-hidden="true" className="hero-logo-glow" width="960" height="960" />
          <img src={LOGO} alt="Pizza Planeta" className="hero-logo-img" width="960" height="960" fetchPriority="high" />
        </div>
        <p className="hero-sub">Sabor de otro mundo</p>
        <div className="hero-divider"></div>
        <p className="hero-tagline">Menú {new Date().getFullYear()}</p>
        <a href={primeraSeccion} className="scroll-hint">
          <span>Ver menú</span>
          <span>↓</span>
        </a>
      </section>

      {/* NAV */}
      <nav className="menu-nav">
        <div className="nav-inner">
          <a href={primeraSeccion} className="nav-brand" onClick={closeMenu}>
            <div className="nav-logo-img" style={{ backgroundImage: `url(${LOGO})` }} />
          </a>

          {/* Links desktop */}
          <ul className="nav-links-desktop">
            {navLinks.map(l => (
              <li key={l.href}><a href={l.href}>{l.label}</a></li>
            ))}
          </ul>

          {/* Hamburguesa solo mobile */}
          <button
            className={`hamburger ${menuOpen ? 'open' : ''}`}
            onClick={() => setMenuOpen(o => !o)}
            aria-label="Menú"
          >
            <span /><span /><span />
          </button>
        </div>

        {/* Dropdown */}
        <div className={`nav-dropdown ${menuOpen ? 'visible' : ''}`}>
          {navLinks.map(l => (
            <a key={l.href} href={l.href} onClick={e => handleNavClick(e, l.href)}>{l.label}</a>
          ))}
        </div>
      </nav>

      {/* PROMOS */}
      {promoMostrar && (
        <section id="promos" className="seccion bg-alt promos-seccion">
          <div className="promo-deco promo-deco-a" aria-hidden="true"><img src="/assets/imgs/pizza-pepperoni.webp" alt="" width="900" height="900" loading="lazy" decoding="async" /></div>
          <div className="promo-deco promo-deco-b" aria-hidden="true"><img src="/assets/imgs/pizza-hawaiana.webp" alt="" width="900" height="852" loading="lazy" decoding="async" /></div>
          <div className="container promos-container">
            <SeccionHeader eyebrow="Ofertas de la semana" titulo="Promos" />
            <div className="promo-hoy-wrap">
              <div className={`promo-hoy ${promoHoy ? '' : 'promo-hoy-off'}`}>
                <div className="promo-hoy-badge">{promoHoy ? '🔥 Promo de hoy' : '¡Te esperamos pronto!'}</div>
                <h3 className="promo-hoy-dia">{promoMostrar.dia}</h3>
                <div className="promo-hoy-fotos">
                  {(promoMostrar.incluye || []).filter(k => PROMO_ITEMS[k]).map((k, i) => (
                    <div key={i} className="promo-hoy-item">
                      {i > 0 && <span className="promo-plus">+</span>}
                      <div className="promo-hoy-foto" style={{ animationDelay: `${i * 0.35}s` }}>
                        <img src={PROMO_ITEMS[k].img} alt={PROMO_ITEMS[k].label} loading="lazy" decoding="async" />
                        <span>{PROMO_ITEMS[k].label}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="promo-hoy-desc">{promoMostrar.descripcion}</p>
                <div className="promo-hoy-precio">${promoMostrar.precio}</div>
                <a
                  className="promo-hoy-cta"
                  href={`https://wa.me/52${contacto.whatsapp}?text=${encodeURIComponent(`Hola, quiero la promo del ${promoMostrar.dia}: ${promoMostrar.descripcion} ($${promoMostrar.precio})`)}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Pedir por WhatsApp
                </a>
                {!promoHoy && <p className="promo-hoy-nota">Hoy ({hoyNombre.toLowerCase()}) no hay promo. Estas son las de la semana.</p>}
              </div>
            </div>

            <p className="promos-sub">Todas las promos de la semana</p>
            <div className="promos-tabla">
              <div className="promos-head">
                <span>Día</span>
                <span>Promo</span>
                <span>Precio</span>
              </div>
              {promosActivas.map(p => (
                <div key={p.promoId} className={`promos-row ${p.dia === hoyNombre ? 'promos-row-hoy' : ''}`}>
                  <span className="promos-dia">{p.dia}</span>
                  <span className="promos-desc">{p.descripcion}</span>
                  <span className="promos-precio">${p.precio}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SECCIONES DEL MENÚ (vienen de DynamoDB) */}
      {principales.map((s, i) => (
        <section key={s.seccionId} id={s.seccionId} className={`seccion ${i % 2 === 1 ? 'bg-alt' : ''}`}>
          <div className="container">
            <SeccionHeader eyebrow={s.eyebrow} titulo={s.nombre} />

            {(s.productos.length > 0 || (s.imagenes || []).length > 0) && (
              s.hijos.length > 0
                ? <div className="subseccion-bloque"><SeccionMenu seccion={s} productos={s.productos} /></div>
                : <SeccionMenu seccion={s} productos={s.productos} />
            )}

            {s.hijos.map(h => (
              <div key={h.seccionId} className="subseccion-bloque">
                <SubHeader titulo={h.nombre} />
                <SeccionMenu seccion={h} productos={h.productos} />
              </div>
            ))}
          </div>
        </section>
      ))}

      {/* WHATSAPP FLOTANTE */}
      <a
        href={`https://wa.me/52${contacto.whatsapp}`}
        target="_blank"
        rel="noreferrer"
        className="whatsapp-float"
        aria-label="WhatsApp"
      >
        <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
          <circle cx="24" cy="24" r="24" fill="#25D366" />
          <path d="M24 10C16.268 10 10 16.268 10 24c0 2.516.676 4.876 1.857 6.91L10 38l7.317-1.82A13.94 13.94 0 0024 38c7.732 0 14-6.268 14-14S31.732 10 24 10z" fill="#25D366" />
          <path fillRule="evenodd" clipRule="evenodd" d="M24 12c-6.627 0-12 5.373-12 12 0 2.196.591 4.254 1.624 6.02L12 37l7.12-1.87A11.94 11.94 0 0024 36c6.627 0 12-5.373 12-12S30.627 12 24 12zm-4.17 6.5c.22 0 .46.01.67.02.24.01.5.03.74.57.29.65 1.01 2.48 1.1 2.66.09.18.15.39.03.63-.12.24-.18.39-.36.6-.18.21-.37.47-.53.63-.18.18-.36.37-.16.72.21.35.92 1.52 1.97 2.46 1.35 1.21 2.49 1.58 2.84 1.76.35.18.55.15.76-.09.21-.24.89-1.04 1.13-1.39.24-.35.48-.29.81-.17.33.12 2.08.98 2.44 1.16.35.18.59.27.67.41.09.14.09.83-.21 1.63-.3.8-1.73 1.57-2.38 1.63-.65.06-1.26.3-4.24-.89-3.57-1.41-5.83-5.04-6.01-5.28-.18-.24-1.47-1.96-1.47-3.74 0-1.78.93-2.65 1.26-3.01.33-.36.72-.45.96-.45z" fill="white" />
        </svg>
      </a>

      {/* FOOTER */}
      <footer className="footer">
        <div className="footer-logo-img" style={{ backgroundImage: `url(${LOGO})` }} />
        <div className="footer-tagline">Sabor de otro mundo</div>
        <div className="footer-contacto">
          <a href={contacto.facebook_url} target="_blank" rel="noreferrer">
            📘 {contacto.facebook}
          </a>
          <a href={`https://wa.me/52${contacto.whatsapp}`} target="_blank" rel="noreferrer">
            📱 {contacto.whatsapp}
          </a>
          <a href={`tel:${contacto.telefono}`}>
            📞 {contacto.telefono}
          </a>
          <a href={contacto.maps_url} target="_blank" rel="noreferrer">
            📍 {contacto.ciudad}
          </a>
        </div>
        <div className="footer-copy">© {new Date().getFullYear()} Pizza Planeta · Todos los derechos reservados</div>
      </footer>
    </>
  );
}
